import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { DetailedIndicatorC1Data } from "@/services/esus/indicator/esf/c1/getDetailedIndicatorC1";
import { Paginated } from "@/@types/generics/paginated";
import {
	CitizenWithAttendancesData,
	getCitizenWithAttendances,
} from "@/services/esus/citizen/getCitizenWithAttendances";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";

import { IndicatorEsfC1AttendancesFilters } from "./IndicatorEsfC1AttendancesFilters";
import { IndicatorEsfC1AttendancesTable } from "./IndicatorEsfC1AttendancesTable";
import { IndicatorEsfC1CitizenHistory } from "./IndicatorEsfC1CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC1AttendancesProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedAttendances: Paginated<DetailedIndicatorC1Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedIndicatorC1: () => Promise<DetailedIndicatorC1Data[]>;
};

export function IndicatorEsfC1Attendances({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedAttendances,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC1AttendancesProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAttendances, setCitizenWithAttendances] =
		useState<CitizenWithAttendancesData>(new CitizenWithAttendancesData());
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(citizenId: number) {
		setIsLoadingCitizenWithAttendances(true);
		const quarterDates = getQuarterDates(selectedQuarter);
		const startDate = dayjs(quarterDates.startDate).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
		const endDate = dayjs(quarterDates.endDate).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
		setCitizenWithAttendances(
			await getCitizenWithAttendances({ uf, ibgeCode, startDate, endDate, citizenId })
		);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC1AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Atendimentos - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC1AttendancesToGenericObjectArray(
		indicatorC1Attendances: DetailedIndicatorC1Data[]
	) {
		return indicatorC1Attendances.map(
			(attendance) =>
				({
					"Data do atendimento": dayjs(attendance.attendanceDate).format("DD/MM/YYYY"),
					Programado: attendance.isScheduled ? "SIM" : "NÃO",
					"Tipo de atendimento": attendance.attendanceTypeName ?? "",
					Nome: attendance.citizenName,
					DN: attendance.birthdate
						? dayjs(attendance.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: attendance.birthdate ? getAge(dayjs(attendance.birthdate)) : "",
					Documento: attendance.cpf ?? attendance.cns ?? "",
					"Equipe do atendimento": attendance.attendanceHealthTeamName ?? "",
					"Equipe do atendimento INE": attendance.attendanceHealthTeamIne ?? "",
					MC: attendance.area ?? "",
					"Unidade do atendimento": attendance.attendanceHealthUnitName,
					"Unidade do atendimento Cnes": attendance.attendanceHealthUnitCnes,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC1AttendancesFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC1AttendancesTable
							attendances={paginatedAttendances.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedAttendances?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedAttendances.pageSize}
								totalPages={paginatedAttendances.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos nos últimos 12 meses
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC1CitizenHistory
							citizenWithAttendances={citizenWithAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
